import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { OAUTH_PROVIDERS, type OAuthProviderSlug } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { Alert, Button, Field } from '@/components/ui';
import { ApiError } from '@/services/api-client';
import { authApi } from '@/services/auth.api';
import { LocalePicker } from '@/components/locale-picker';
import { LogoMark } from '@/components/logo-mark';
import { OAUTH_ICONS } from '@/components/oauth-icons';

/** How many active providers show directly in the button row before the
 *  rest collapse behind "more options" — not a fixed Google/Microsoft/Apple
 *  list, so a lone active provider (say, only GitHub) always shows up front
 *  rather than hiding behind a toggle nobody has a reason to click. */
const MAX_PRIMARY_PROVIDERS = 3;

export function LoginPage() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMoreProviders, setShowMoreProviders] = useState(false);

  const providers = useQuery({
    queryKey: ['auth', 'oauth-providers'],
    queryFn: () => authApi.getOAuthProviders(),
  });

  const activeProviders = OAUTH_PROVIDERS.filter((provider) => providers.data?.[provider]);
  const availablePrimary = activeProviders.slice(0, MAX_PRIMARY_PROVIDERS);
  const availableMore = activeProviders.slice(MAX_PRIMARY_PROVIDERS);
  const hasAnyProvider = activeProviders.length > 0;

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    // `void` marks the promise as deliberately unawaited: React's onSubmit
    // expects a void return, and every failure path is handled inside.
    void submit();
  }

  async function submit(): Promise<void> {
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await login(email, password, rememberMe);

      if ('mfaRequired' in result) {
        // The token travels as router state, not a URL — a bearer credential,
        // however short-lived, does not belong in browser history or a log line.
        void navigate('/mfa-challenge', {
          replace: true,
          state: {
            mfaToken: result.mfaToken,
            availableFactors: result.availableFactors,
            rememberMe,
          },
        });
        return;
      }

      // A user in several churches must choose one before anything is scoped.
      // `navigate` returns a promise in react-router 8; nothing here needs to
      // wait for the transition to settle.
      void navigate(result.tenantSelected ? '/' : '/select-church', { replace: true });
    } catch (caught) {
      // The message is looked up by the API's error code, so it is rendered in
      // the user's language rather than in the server's English.
      const code = caught instanceof ApiError ? caught.code : 'INTERNAL_ERROR';
      setError(t(`errors.${code}`, { fallback: t('errors.INTERNAL_ERROR') }));
    } finally {
      setIsSubmitting(false);
    }
  }

  function signInWith(provider: OAuthProviderSlug): void {
    window.location.href = authApi.oauthStartUrl(provider, rememberMe);
  }

  return (
    <main className="auth-page">
      <div className="auth-card">
        <header className="auth-header">
          <LogoMark size={40} />
          <h1 className="auth-title">TABITA</h1>
          <p className="auth-subtitle">{t('auth.signIn')}</p>
        </header>

        {hasAnyProvider && (
          <div className="oauth-buttons">
            {availablePrimary.map((provider) => (
              <button
                key={provider}
                type="button"
                className="oauth-button"
                onClick={() => signInWith(provider)}
              >
                {OAUTH_ICONS[provider]()}
                {t(`auth.oauth.continueWith.${provider}`)}
              </button>
            ))}

            {availableMore.length > 0 && !showMoreProviders && (
              <button
                type="button"
                className="oauth-more-toggle"
                onClick={() => setShowMoreProviders(true)}
              >
                {t('auth.oauth.moreOptions')}
              </button>
            )}

            {showMoreProviders &&
              availableMore.map((provider) => (
                <button
                  key={provider}
                  type="button"
                  className="oauth-button"
                  onClick={() => signInWith(provider)}
                >
                  {OAUTH_ICONS[provider]()}
                  {t(`auth.oauth.continueWith.${provider}`)}
                </button>
              ))}

            <div className="oauth-divider">
              <span>{t('auth.oauth.orDivider')}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="auth-form">
          {error && <Alert tone="danger">{error}</Alert>}

          <Field
            label={t('auth.email')}
            name="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoComplete="username"
            // Focused on load: this is the only thing to do on this page.
            autoFocus
            // `dir="ltr"` regardless of locale — an email address is always
            // left-to-right, and rendering one RTL scrambles it visually.
            dir="ltr"
          />

          <Field
            label={t('auth.password')}
            name="password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            autoComplete="current-password"
            dir="ltr"
          />

          <label className="checkbox">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(event) => setRememberMe(event.target.checked)}
            />
            <span>{t('auth.rememberMe')}</span>
          </label>

          <Button type="submit" variant="primary" loading={isSubmitting}>
            {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
          </Button>

          <Link to="/forgot-password" className="auth-secondary-link">
            {t('auth.forgotPassword')}
          </Link>
          <Link to="/register" className="auth-secondary-link">
            {t('auth.registerChurch')}
          </Link>
        </form>

        <footer className="auth-footer">
          <LocalePicker />
        </footer>
      </div>
    </main>
  );
}
